import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type { Text } from 'zrender';
import { type BarSeriesOption } from 'echarts';
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { util } from 'echarts/core';
import BarView from 'echarts/lib/chart/bar/BarView';
import type BarViewType from 'echarts/types/src/chart/bar/BarView';
import { isFunction } from 'zrender/lib/core/util';
import SeriesData, { DefaultDataVisual } from 'echarts/types/src/data/SeriesData';
import BarSeriesModel from 'echarts/types/src/chart/bar/BarSeries';
import Axis2D from 'echarts/types/src/coord/cartesian/Axis2D';
import { overrideIfUndefined } from '../../../util/data';
import { isNumber } from '../../../util/math';
import { measureTextWidth } from '../../../util/utils';

const seriesLabelFontSize = 10;

type OrderMapping = (dataIndex: number) => number;

/**
 * 代码拷贝自echarts
 * 为了实现动态条形图倒序排序的功能
 */
BarView.prototype._dataSort = function (
  data: SeriesData<BarSeriesModel, DefaultDataVisual>,
  baseAxis: Axis2D,
  orderMapping: OrderMapping
) {
  /** 读自定义配置项 */
  let inverse: boolean;
  if (this.__model.option.dvSortType) {
    inverse = this.__model.option.dvSortType === 'asc';
  } else {
    inverse = this.__model.option.dvSortByAsc;
  }
  /** ************** */
  const info = [];
  data.each(data.mapDimension(baseAxis.dim), function (ordinalNumber, dataIdx) {
    let mappedValue = orderMapping(dataIdx);
    mappedValue = mappedValue === null || mappedValue === undefined ? NaN : mappedValue;
    info.push({
      dataIndex: dataIdx,
      mappedValue,
      ordinalNumber
    });
  });
  info.sort(function (a, b) {
    // If NaN, it will be treated as min val.
    if (Number.isNaN(a.mappedValue)) {
      return inverse ? -1 : 1;
    }
    if (Number.isNaN(b.mappedValue)) {
      return inverse ? 1 : -1;
    }
    // 修改了echarts的排序逻辑，原本只有降序，现在如果配置了升序则改变sort
    return inverse ? a.mappedValue - b.mappedValue : b.mappedValue - a.mappedValue;
  });
  return {
    ordinalNumbers: util.map(info, function (item) {
      return item.ordinalNumber as number;
    })
  };
};

function parseBarItem(seriesOption: BarSeriesOption[]) {
  seriesOption.forEach(option => {
    if (typeof option.label === 'undefined') {
      option.label = {
        fontFamily: 'THSJinRongTi-Medium',
        fontSize: seriesLabelFontSize
      };
    } else {
      option.label.fontFamily = overrideIfUndefined(option.label.fontFamily, 'PingFangSC-Regular');
    }
  });
}

function parseBarLabelCount(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('bar', compModel => {
    const dvMaxLabelCount = compModel.get('dvMaxLabelCount');
    if (typeof dvMaxLabelCount === 'undefined') {
      return;
    }
    let barNum;
    const dataZoomModel = api.getModel().getComponent('dataZoom');
    if (dataZoomModel) {
      const startValue = dataZoomModel.get('startValue') as number;
      const endValue = dataZoomModel.get('endValue') as number;
      barNum = endValue - startValue + 1;
    } else {
      barNum = compModel.getData().count();
    }
    const isShow = barNum <= dvMaxLabelCount;
    const barView = api.getViewOfSeriesModel(compModel) as unknown as BarViewType;
    const barItems = barView.group.children().filter(child => child.name === 'item');
    barItems.forEach(rect => {
      const itemLabel = rect._textContent as Text;
      if (!itemLabel) {
        return;
      }

      itemLabel.stopAnimation(null, true);
      if (isShow) {
        itemLabel.show();
      } else {
        itemLabel.hide();
      }
    });
  });
}

function parseAdaptBarLabel(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('bar', compModel => {
    const labelOpt = compModel.get('label');
    if (labelOpt.dvPosition === 'adaptBarWidth') {
      const dvDirection = labelOpt.position || 'right';
      const barView = api.getViewOfSeriesModel(compModel) as unknown as BarViewType;
      const barItems = barView.group.children().filter(child => child.name === 'item');
      barItems.forEach(barItem => {
        const rect = barItem.getBoundingRect();
        const rectWidth = rect.width;
        const textContent = barItem.getTextContent();
        if (!textContent) {
          return;
        }
        const { text, fontSize = 14, fontFamily, fontWeight = 400 } = textContent.style;
        const _fontSize = (
          isNumber(fontSize) ? fontSize : parseFloat(fontSize as string)
        ) as number;
        const textWidth = measureTextWidth(text, _fontSize, fontFamily, fontWeight);
        const isRectBigger = rectWidth > textWidth + 8;
        const align = ['left', 'right'].find(el => {
          if (isRectBigger) {
            return el === dvDirection;
          }
          return el !== dvDirection;
        });
        const padding = dvDirection === 'left' ? [0, 0, 0, 8] : [0, 8, 0, 0];
        textContent.attr('style', {
          ...textContent.style,
          fill: isRectBigger
            ? labelOpt.dvInsideColor || '#fff'
            : labelOpt.dvOutsideColor || compModel.get('itemStyle')?.color,
          align,
          padding
        });
      });
    }
  });
}

function parseBarBorderRadius(ecModel) {
  ecModel.eachSeriesByType('bar', seriesModel => {
    const itemStyle = seriesModel.get('itemStyle');
    const data = seriesModel.getData();
    if (itemStyle && data.count() > 0 && isFunction(itemStyle?.borderRadius)) {
      data.each(i => {
        const rect = data.getItemGraphicEl(i);
        rect.shape.r = itemStyle.borderRadius(data.getRawDataItem(i));
      });
    }
  });
}

/**
 * 柱状图柱子扩展
 */
export function install(registers: EChartsExtensionInstallRegisters) {
  // * ecModel.setOption() 时
  registers.registerPreprocessor(option => {
    const barSeries = (Array.isArray(option.series) ? option.series : [option.series]).filter(
      item => item.type === 'bar'
    );
    if (barSeries.length === 0) {
      return;
    }

    // @ts-ignore
    parseBarItem(barSeries);
  });

  registers.registerUpdateLifecycle('series:layoutlabels', (ecModel, api) => {
    parseBarLabelCount(ecModel, api);
    parseAdaptBarLabel(ecModel, api);
  });
  registers.registerUpdateLifecycle('series:afterupdate', ecModel => {
    parseBarBorderRadius(ecModel);
  });
}
